import { useEffect, useMemo, useRef, useState } from 'react';

function detectProvider(url) {
  const u = (url ?? '').toLowerCase();
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  return 'link';
}

function buildFacebookEmbedUrl(videoUrl) {
  const base = 'https://www.facebook.com/plugins/video.php';
  const params = new URLSearchParams({
    href: videoUrl,
    show_text: 'false',
    width: '560',
  });
  return `${base}?${params.toString()}`;
}

function extractTikTokId(url) {
  try {
    const match = String(url).match(/\/video\/(\d+)/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

function buildTikTokEmbedUrl(videoUrl) {
  const id = extractTikTokId(videoUrl);
  if (!id) return null;
  return `https://www.tiktok.com/embed/v2/${id}`;
}

function useInViewport(options) {
  const ref = useRef(null);
  const [isInViewport, setIsInViewport] = useState(false);

  useEffect(() => {
    if (!ref.current || isInViewport) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsInViewport(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsInViewport(true);
          obs.disconnect();
        }
      },
      options ?? { rootMargin: '250px 0px' }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [isInViewport, options]);

  return { ref, isInViewport };
}

export function VideoEmbed({ title, url }) {
  const { ref, isInViewport } = useInViewport();
  const provider = useMemo(() => detectProvider(url), [url]);
  const embedUrl = useMemo(() => {
    if (provider === 'facebook') return buildFacebookEmbedUrl(url);
    if (provider === 'tiktok') return buildTikTokEmbedUrl(url);
    return null;
  }, [provider, url]);
  const [failed, setFailed] = useState(false);

  return (
    <div className="card section" ref={ref}>
      <div className="titleRow">
        <h3 className="h2">{title}</h3>
        <span className="badge">{provider === 'tiktok' ? 'تيك توك' : provider === 'facebook' ? 'فيسبوك' : 'رابط'}</span>
      </div>

      {failed || !embedUrl ? (
        <p className="muted" style={{ margin: 0, lineHeight: 1.9 }}>
          لا يمكن عرض الفيديو داخل الصفحة. افتحه مباشرة:{' '}
          <a href={url} target="_blank" rel="noreferrer">
            فتح الفيديو
          </a>
        </p>
      ) : isInViewport ? (
        <div
          style={{
            borderRadius: 14,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.04)',
          }}
        >
          <iframe
            title={title}
            src={embedUrl}
            width="100%"
            height={provider === 'tiktok' ? 560 : 315}
            style={{ border: 0, display: 'block' }}
            scrolling="no"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen
            onError={() => setFailed(true)}
          />
        </div>
      ) : (
        <p className="muted" style={{ margin: 0, lineHeight: 1.9 }}>
          يتم تحميل الفيديو عند الوصول إليه… إذا لم يعمل، يمكنك{' '}
          <a href={url} target="_blank" rel="noreferrer">
            فتحه كرابط
          </a>
          .
        </p>
      )}
    </div>
  );
}